import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './ExpertHelpSheet.scss';
import { Trans } from 'react-i18next';
import { getCookie } from '../utils/utils';

type ExpertHelpSheetProps = {
  isVisible: boolean;
  onClose: () => void;
  conversationId?: string | null;
  userRole?: 'BUYER' | 'SELLER' | 'UNKNOWN';
  propertyAddress?: string;
  otherUserEmail?: string;
  isLoggedIn?: boolean;
  onLoginClick?: () => void;
  onRequestSent: () => void;
};

const HARDCODED_TOPICS = ['Finalizing the deal'];

const CheckCircleIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="24" r="22" fill="#E8F5E9" stroke="#4CAF50" strokeWidth="2"/>
    <path d="M16 24L22 30L32 18" stroke="#4CAF50" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ExpertIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ExpertHelpSheet: React.FC<ExpertHelpSheetProps> = ({
  isVisible,
  onClose,
  conversationId,
  userRole,
  propertyAddress,
  otherUserEmail,
  isLoggedIn,
  onLoginClick,
  onRequestSent,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const [step, setStep] = useState<1 | 2>(1);
  const [contactMethod, setContactMethod] = useState<'phone' | 'email'>('phone');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [prefillLoaded, setPrefillLoaded] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  useEffect(() => {
    if (isVisible && isLoggedIn && !prefillLoaded) {
      fetchPrefill();
    }
  }, [isVisible, isLoggedIn, prefillLoaded]);

  useEffect(() => {
    if (!isVisible) {
      setStep(1);
      setContactMethod('phone');
      setIsSubmitting(false);
      setPrefillLoaded(false);
      setLimitReached(false);
    }
  }, [isVisible]);

  const fetchPrefill = async () => {
    try {
      const prefillUrl = conversationId
        ? `${process.env.REACT_APP_API_URL}/conversations/${conversationId}/expert-help-prefill`
        : `${process.env.REACT_APP_API_URL}/expert-help-prefill`;
      const response = await fetch(
        prefillUrl,
        {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.email) {
          setEmail(currentEmail => currentEmail.trim() ? currentEmail : data.email);
        }
        if (data.phone) {
          setPhone(currentPhone => currentPhone.trim() ? currentPhone : data.phone);
        }
        if (data.pending_request_count >= 3) setLimitReached(true);
      }
    } catch (err) {
      // Prefill is best-effort
    } finally {
      setPrefillLoaded(true);
    }
  };

  /**
   * Submit the expert-help request. Signed out, it closes the sheet first and
   * then opens login, so the auth modal (z 2800) never opens under this sheet
   * (z 10001) for any caller (JAA-325).
   */
  const handleSubmit = async () => {
    if (isSubmitting) return;

    if (!isLoggedIn) {
      onClose();
      onLoginClick?.();
      return;
    }

    if (contactMethod === 'phone' && !phone.trim()) return;
    if (contactMethod === 'email' && !email.trim()) return;

    setIsSubmitting(true);
    try {
      const submitUrl = conversationId
        ? `${process.env.REACT_APP_API_URL}/conversations/${conversationId}/expert-help-request`
        : `${process.env.REACT_APP_API_URL}/expert-help-request`;
      const response = await fetch(
        submitUrl,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': getCookie('csrf_access_token'),
          },
          body: JSON.stringify({
            topics: HARDCODED_TOPICS,
            contact_method: contactMethod,
            phone: phone.trim(),
            email: email.trim(),
            property_address: propertyAddress,
            other_party_name: otherUserEmail,
            user_role: userRole,
          }),
        }
      );

      if (response.status === 429) {
        setLimitReached(true);
        return;
      }

      if (!response.ok) {
        throw new Error('Failed to submit');
      }

      setStep(2);
    } catch (err) {
      console.error('Error submitting expert help request:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToChat = () => {
    onRequestSent();
    onClose();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      if (step === 2) {
        handleBackToChat();
      } else {
        onClose();
      }
    }
  };

  if (!isVisible) return null;

  return createPortal(
    <div className="expert-help-overlay" onClick={handleBackdropClick}>
      <div className="expert-help-sheet" ref={sheetRef}>
        <div className="expert-help-sheet__handle" />

        {limitReached && (
          <div className="expert-help-sheet__step expert-help-sheet__step--limit">
            <div className="expert-help-sheet__header">
              <button className="expert-help-sheet__close" onClick={onClose}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
            <h2 className="expert-help-sheet__title" style={{ textAlign: 'center', marginTop: 20 }}>
              <Trans i18nKey="messages.expertHelp.limitTitle">Request limit reached</Trans>
            </h2>
            <p className="expert-help-sheet__subtitle" style={{ textAlign: 'center' }}>
              <Trans i18nKey="messages.expertHelp.limitMessage">
                You have reached the maximum number of open help requests. We'll process your existing requests first.
              </Trans>
            </p>
            <button
              className="expert-help-sheet__primary-button"
              onClick={onClose}
            >
              <Trans i18nKey="messages.expertHelp.backToChat">Back to chat</Trans>
            </button>
          </div>
        )}

        {!limitReached && step === 1 && (
          <div className="expert-help-sheet__step">
            <div className="expert-help-sheet__header">
              <button className="expert-help-sheet__close" onClick={onClose}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            <h2 className="expert-help-sheet__title">
              <Trans i18nKey="messages.expertHelp.title">Get expert help with your deal</Trans>
            </h2>
            <div className="expert-help-sheet__pricing" role="note">
              <p className="expert-help-sheet__pricing-body">
                <Trans i18nKey="messages.expertHelp.pricingBody">
                  Found a buyer or seller? Need help completing the sale?{'\n\n'}
                  Our expert partner will complete the housing-share or real estate transaction on your behalf. No brokerage agreement. No commission.{'\n\n'}
                  Price: 990 €
                </Trans>
              </p>
            </div>

            <div className="expert-help-sheet__form">
              <label className="expert-help-sheet__label">
                <Trans i18nKey="messages.expertHelp.contactLabel">How would you like us to contact you?</Trans>
              </label>
              <div className="expert-help-sheet__radio-group">
                <button
                  className={`expert-help-sheet__radio ${contactMethod === 'phone' ? 'expert-help-sheet__radio--selected' : ''}`}
                  onClick={() => setContactMethod('phone')}
                >
                  <Trans i18nKey="messages.expertHelp.callMe">Call me</Trans>
                </button>
                <button
                  className={`expert-help-sheet__radio ${contactMethod === 'email' ? 'expert-help-sheet__radio--selected' : ''}`}
                  onClick={() => setContactMethod('email')}
                >
                  <Trans i18nKey="messages.expertHelp.emailMe">Email me</Trans>
                </button>
              </div>

              {contactMethod === 'phone' && (
                <div className="expert-help-sheet__field">
                  <label className="expert-help-sheet__label">
                    <Trans i18nKey="messages.expertHelp.phoneLabel">Phone number</Trans>
                  </label>
                  <input
                    className="expert-help-sheet__input"
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="+358 40 123 4567"
                  />
                </div>
              )}

              {contactMethod === 'email' && (
                <div className="expert-help-sheet__field">
                  <label className="expert-help-sheet__label">
                    <Trans i18nKey="messages.expertHelp.emailLabel">Email</Trans>
                  </label>
                  <input
                    className="expert-help-sheet__input"
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
              )}
            </div>

            <p className="expert-help-sheet__disclaimer">
              <Trans i18nKey="messages.expertHelp.freeDisclaimer">
                The conversation is non-binding and free of charge.
              </Trans>
            </p>

            <button
              className="expert-help-sheet__primary-button"
              onClick={handleSubmit}
              disabled={isSubmitting || (contactMethod === 'phone' && !phone.trim()) || (contactMethod === 'email' && !email.trim())}
            >
              {isSubmitting ? (
                <span className="expert-help-sheet__spinner" />
              ) : (
                <Trans i18nKey="messages.expertHelp.sendRequest">Send request</Trans>
              )}
            </button>
          </div>
        )}

        {!limitReached && step === 2 && (
          <div className="expert-help-sheet__step expert-help-sheet__step--success">
            <CheckCircleIcon />
            <h2 className="expert-help-sheet__title">
              <Trans i18nKey="messages.expertHelp.successTitle">Request sent</Trans>
            </h2>
            <p className="expert-help-sheet__subtitle">
              <Trans i18nKey="messages.expertHelp.successMessage">
                We'll review your situation and contact you by phone or email.
              </Trans>
            </p>
            <p className="expert-help-sheet__response-time">
              <Trans i18nKey="messages.expertHelp.responseTime">
                Typical response time: within 1 business day
              </Trans>
            </p>
            <button
              className="expert-help-sheet__primary-button"
              onClick={handleBackToChat}
            >
              <Trans i18nKey="messages.expertHelp.backToChat">Back to chat</Trans>
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

export { ExpertIcon };
export default ExpertHelpSheet;
